import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Image } from 'primereact/image';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { arrayIncludes } from '@material-ui/pickers/_helpers/utils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import utils from '../../helpers/GlobalVariables';
import { fetchUrl } from '../../stores/actions/settings';
import ServiceActionPerformersField from './ServiceActionPerformers';
import { getServiceActionAttachments, clearServiceActionAttachments, addServiceActionAttachment, deleteServiceActionAttachment,
         getServiceActionPartImages, clearServiceActionPartImages, addServiceActionPartImage, deleteServiceActionPartImage } from '../../stores/actions/installerActions';

const MAX_ATTACHMENTS = 5;
const MAX_PART_IMAGES = 5;

interface ServiceActionFormDialogProps {
    visible: boolean;
    mobileView: boolean;
    title: string;
    value: any;
    onChange: (patch: any) => void;
    onHide: () => void;
    onSave: () => void;
    systemNames?: string[];
    fixedSystemName?: string;
    fixedDate?: string;
    serviceActionAttachments: any[];
    getServiceActionAttachments: (serviceActionId: number) => void;
    clearServiceActionAttachments: () => void;
    addServiceActionAttachment: (attachment) => void;
    deleteServiceActionAttachment: (attachmentId: number) => void;
    serviceActionPartImages: any[];
    getServiceActionPartImages: (serviceActionId: number) => void;
    clearServiceActionPartImages: () => void;
    addServiceActionPartImage: (image) => void;
    deleteServiceActionPartImage: (imageId: number) => void;
}

const ServiceActionFormDialog = ({
    visible,
    mobileView,
    title,
    value,
    onChange,
    onHide,
    onSave,
    systemNames,
    fixedSystemName,
    fixedDate,
    serviceActionAttachments,
    getServiceActionAttachments,
    clearServiceActionAttachments,
    addServiceActionAttachment,
    deleteServiceActionAttachment,
    serviceActionPartImages,
    getServiceActionPartImages,
    clearServiceActionPartImages,
    addServiceActionPartImage,
    deleteServiceActionPartImage,
}: ServiceActionFormDialogProps) => {
    const removedPartImageUploadRef = useRef<any>(null);
    const installedPartImageUploadRef = useRef<any>(null);
    const attachmentUploadRef = useRef<any>(null);

    const [uploadError, setUploadError] = useState<{ source: string; message: string } | null>(null);

    const savedServiceActionId = value?.ID > 0 ? value.ID : null;

    useEffect(() => {
        setUploadError(null);
        if (visible && savedServiceActionId) {
            getServiceActionAttachments(savedServiceActionId);
            getServiceActionPartImages(savedServiceActionId);
        } else {
            clearServiceActionAttachments();
            clearServiceActionPartImages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, savedServiceActionId]);

    const validators = {
        description: (removedPartDesc: string | null, installedPartDesc: string | null) => !!removedPartDesc || !!installedPartDesc,
    };

    function formatDateRemoveTime(date) {
        return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
    }

    const onInputChange = (e, name) => {
        let val = e.value !== undefined ? e.value : e.target?.value ?? null;
        if (arrayIncludes(['date'], name)) {
            val = formatDateRemoveTime(val);
        }
        onChange({ [name]: val });
    };

    const onInputWarrantyChange = (e) => {
        onChange({ underWarranty: e.target && e.target.checked });
    };

    const partsDisabled = !!value.batteryReplacement;

    const pendingPartImages = value.pendingPartImages || [];

    const partImageTiles = (partKind: 'removedPart' | 'installedPart') => {
        const tiles = [];
        const legacyData = partKind === 'removedPart' ? value.removedPartImageData : value.installedPartImageData;
        const legacyFileType = partKind === 'removedPart' ? value.removedPartImageFileType : value.installedPartImageFileType;
        if (legacyData) {
            tiles.push({
                key: 'legacy',
                src: `data:${legacyFileType};base64,${legacyData}`,
                onRemove: () => onChange(partKind === 'removedPart'
                    ? { removedPartImageData: null, removedPartImageFileType: null }
                    : { installedPartImageData: null, installedPartImageFileType: null }),
            });
        }
        (serviceActionPartImages || [])
            .filter((img) => img.partKind === partKind.replace('Part', ''))
            .forEach((img) => tiles.push({
                key: `saved-${img.id}`,
                src: `data:${img.imageFileType};base64,${img.imageData}`,
                onRemove: () => deleteServiceActionPartImage(img.id),
            }));
        pendingPartImages
            .filter((img) => img.partKind === partKind.replace('Part', ''))
            .forEach((img) => tiles.push({
                key: `pending-${img._tempId}`,
                src: `data:${img.imageFileType};base64,${img.imageData}`,
                onRemove: () => onChange({ pendingPartImages: pendingPartImages.filter((p) => p._tempId !== img._tempId) }),
            }));
        return tiles;
    };

    const handleAddPartImage = (e, partKind: 'removedPart' | 'installedPart', ref) => {
        const file = e.files[0];
        if (!file) return;
        setUploadError(null);
        const reader = new FileReader();

        const clearUpload = () => {
            if (ref.current) {
                ref.current.clear();
            }
        };

        reader.onloadend = () => {
            const imageData = (reader.result as string).split(',')[1];
            const imageFileType = file.type;
            const kind = partKind.replace('Part', '');
            if (savedServiceActionId) {
                addServiceActionPartImage({ serviceActionId: savedServiceActionId, partKind: kind, imageData, imageFileType });
            } else {
                onChange({ pendingPartImages: [...pendingPartImages, { _tempId: Date.now() + Math.random(), partKind: kind, imageData, imageFileType }] });
            }
            clearUpload();
        };
        reader.onerror = () => {
            setUploadError({ source: partKind, message: `Could not read "${file.name}". Please try again.` });
            clearUpload();
        };

        reader.readAsDataURL(file);
    };

    const renderPartImages = (partKind: 'removedPart' | 'installedPart', uploadRef, label: string) => {
        const tiles = partImageTiles(partKind);
        return (
            <>
                <label className="mb-3 font-bold flex">{label}</label>

                {tiles.length > 0 ? (
                    <div className="flex flex-wrap" style={{ gap: '10px' }}>
                        {tiles.map((tile) => (
                            <div
                                key={tile.key}
                                className="relative border rounded shadow-sm flex items-center justify-center overflow-hidden"
                                style={{ height: '150px', width: '150px', flexDirection: 'column', textAlign: 'center' }}
                            >
                                <Image
                                    src={tile.src}
                                    alt={`${label} image`}
                                    width="100%"
                                    preview
                                    style={{ objectFit: 'contain' }}
                                    className="border rounded shadow-sm"
                                />
                                <button
                                    type="button"
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition"
                                    onClick={tile.onRemove}
                                >
                                    <i className="pi pi-times text-sm"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400"
                        style={{ height: '150px', flexDirection: 'column', textAlign: 'center' }}>
                        <i className="pi pi-image text-6xl mt-5" />
                        <span className="text-xs mt-1">No image</span>
                    </div>
                )}

                <div className="items-center gap-4 mt-3">
                    {tiles.length >= MAX_PART_IMAGES ? (
                        <small className="text-color-secondary">Maximum of {MAX_PART_IMAGES} images reached.</small>
                    ) : (
                        <FileUpload
                            ref={uploadRef}
                            mode="basic"
                            accept="image/*"
                            maxFileSize={5000000}  // 5 MB
                            customUpload
                            auto
                            chooseLabel="Add Image"
                            disabled={partsDisabled}
                            onSelect={(e) => handleAddPartImage(e, partKind, uploadRef)}
                            onValidationFail={(file) => setUploadError({ source: partKind, message: `"${file.name}" is too large - maximum size is 5 MB.` })}
                        />
                    )}
                    {uploadError?.source === partKind && (
                        <small className="p-error block mt-1">{uploadError.message}</small>
                    )}
                </div>
            </>
        );
    };

    const pendingAttachments = value.pendingAttachments || [];
    const attachmentsToShow = savedServiceActionId ? serviceActionAttachments : pendingAttachments;

    const handleAttachmentUpload = (e) => {
        const file = e.files[0];
        if (!file) return;
        setUploadError(null);

        const clearUpload = () => {
            if (attachmentUploadRef.current) {
                attachmentUploadRef.current.clear();
            }
        };

        const reader = new FileReader();
        reader.onloadend = () => {
            const newAttachment = {
                fileData: (reader.result as string).split(',')[1],
                fileMimeType: file.type,
                fileSize: file.size,
                fileName: file.name,
            };
            if (savedServiceActionId) {
                addServiceActionAttachment({ serviceActionId: savedServiceActionId, ...newAttachment });
            } else {
                onChange({ pendingAttachments: [...pendingAttachments, { ...newAttachment, _tempId: Date.now() + Math.random() }] });
            }
            clearUpload();
        };
        reader.onerror = () => {
            setUploadError({ source: 'attachment', message: `Could not read "${file.name}". Please try again.` });
            clearUpload();
        };
        reader.readAsDataURL(file);
    };

    const removePendingAttachment = (tempId) => {
        onChange({ pendingAttachments: pendingAttachments.filter((a) => a._tempId !== tempId) });
    };

    const openAttachment = async (attachment) => {
        if (!savedServiceActionId) {
            window.open(`data:${attachment.fileMimeType};base64,${attachment.fileData}`, '_blank');
            return;
        }
        const token = localStorage.getItem('w4-token');
        const resp = await fetch(`${fetchUrl}/service-action-attachment-data/${attachment.id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        window.open(`data:${data.fileMimeType};base64,${data.fileData}`, '_blank');
    };

    const formatFileSize = (bytes: number) => {
        if (!bytes) return '0 KB';
        const kb = bytes / 1024;
        return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.ceil(kb)} KB`;
    };

    const footer = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={onHide} />
            <Button label="Save" icon="pi pi-check" onClick={onSave} />
        </React.Fragment>
    );

    return (
        <Dialog visible={visible}
                style={{ width: mobileView ? '100%' : '90rem' }}
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                modal className="p-fluid" footer={footer} onHide={onHide}
                header={title}
                >
            <div className="formgrid grid">
                <div className="field">
                    <label className="mb-3 font-bold">System</label>
                    {systemNames ? (
                        <Dropdown value={value.systemName} onChange={(e) => onInputChange(e, 'systemName')} options={systemNames} optionLabel="name"
                            filter placeholder="Select system" className="w-full md:w-28rem" invalid={!value.systemName} />
                    ) : (
                        <Dropdown value={fixedSystemName ?? value.systemName ?? ''} options={[fixedSystemName ?? value.systemName ?? '']}
                            disabled className="w-full md:w-28rem" />
                    )}
                </div>
                <div className="field" style={{ marginLeft: mobileView ? '0px' : '10px' }}>
                    <label className="mb-3 font-bold">Date</label>
                    {fixedDate ? (
                        <Calendar value={new Date(fixedDate)} disabled dateFormat="yy-mm-dd" />
                    ) : (
                        <Calendar value={new Date(value.date)} onChange={(e) => onInputChange(e, 'date')} dateFormat="yy-mm-dd" invalid={!value.date} />
                    )}
                </div>
                <div className="field flex align-items-center" style={{ marginTop: '10px', marginLeft: '10px' }}>
                    <Checkbox inputId="saUnderWarranty" onChange={(e) => onInputWarrantyChange(e)} checked={value.underWarranty} value={value.underWarranty}></Checkbox>
                    <label htmlFor="saUnderWarranty" style={{ marginBottom: '0px', marginLeft: '15px' }}>Under warranty</label>
                </div>
            </div>
            <div className="field">
                <label className="mb-3 font-bold">Performed by</label>
                <ServiceActionPerformersField value={value} onChange={onChange} />
            </div>
            {value.batteryReplacement && (
                <div className="field flex align-items-center">
                    <Tag value="CU battery replacement" severity="warning"></Tag>
                </div>
            )}
            <div className="formgrid grid" style={{ opacity: partsDisabled ? 0.5 : 1, pointerEvents: partsDisabled ? 'none' : 'auto' }}>
                <div className="field col">
                    <Fieldset legend="Removed part">
                        <div className="field col">
                            <label className={"mb-3 font-bold"}>Description</label>
                            <InputText value={value.removedPartDesc} maxLength={utils.defaultMaxInputLength} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'removedPartDesc')} />
                            {!partsDisabled && !validators.description(value.removedPartDesc, value.installedPartDesc) && (
                                <small className="p-error">At least one Description field must be filled.</small>
                            )}<br />

                            <label className="mb-3 font-bold">Name in System</label>
                            <InputText value={value.removedPartName} maxLength={utils.defaultMaxInputLength} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'removedPartName')} />

                            <label className="mb-3 font-bold">Serial number</label>
                            <InputText value={value.removedPartSerialNumber} maxLength={128} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'removedPartSerialNumber')} />

                            {renderPartImages('removedPart', removedPartImageUploadRef, 'Images')}
                        </div>
                    </Fieldset>
                </div>
                <div className="field col">
                    <Fieldset legend="Installed part">
                        <div className="field col">
                            <label className="mb-3 font-bold">Description</label>
                            <InputText value={value.installedPartDesc} maxLength={utils.defaultMaxInputLength} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'installedPartDesc')} />

                            <label className="mb-3 font-bold">Name in System</label>
                            <InputText value={value.installedPartName} maxLength={utils.defaultMaxInputLength} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'installedPartName')} />

                            <label className="mb-3 font-bold">Serial number</label>
                            <InputText value={value.installedPartSerialNumber} maxLength={128} disabled={partsDisabled} onChange={(e) => onInputChange(e, 'installedPartSerialNumber')} />

                            {renderPartImages('installedPart', installedPartImageUploadRef, 'Images')}
                        </div>
                    </Fieldset>
                </div>
            </div>
            <div className="field">
                <label className="mb-3 font-bold">Notes</label>
                <ReactQuill
                    theme="snow"
                    value={value.notes || ''}
                    onChange={(notes) => onChange({ notes })}
                />
            </div>
            <div className="field">
                <label className="mb-3 font-bold">Attachments</label>

                {!savedServiceActionId && (
                    <small className="text-color-secondary block mb-2">
                        Not saved yet &mdash; these will be uploaded together with the service action.
                    </small>
                )}

                {attachmentsToShow.map((att) => (
                    <div
                        key={savedServiceActionId ? att.id : att._tempId}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px'
                        }}
                    >
                        <span style={{ wordBreak: 'break-word' }}>
                            <b>{att.fileName}</b> ({formatFileSize(att.fileSize)})
                        </span>

                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            onClick={() => openAttachment(att)}
                            tooltip="Open file"
                        />

                        <Button
                            icon="pi pi-times"
                            className="p-button-text p-button-danger"
                            onClick={() => savedServiceActionId ? deleteServiceActionAttachment(att.id) : removePendingAttachment(att._tempId)}
                            tooltip="Remove file"
                        />
                    </div>
                ))}

                {attachmentsToShow.length >= MAX_ATTACHMENTS ? (
                    <small className="text-color-secondary">Maximum of {MAX_ATTACHMENTS} attachments reached.</small>
                ) : (
                    <FileUpload
                        ref={attachmentUploadRef}
                        mode="basic"
                        name="attachmentFile"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        maxFileSize={10000000} // 10 MB
                        customUpload
                        auto
                        chooseLabel="Select File"
                        uploadHandler={handleAttachmentUpload}
                        className="mb-2"
                        onValidationFail={(file) => setUploadError({ source: 'attachment', message: `"${file.name}" is too large - maximum size is 10 MB.` })}
                    />
                )}
                {uploadError?.source === 'attachment' && (
                    <small className="p-error block mt-1">{uploadError.message}</small>
                )}
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state) => {
    return {
        serviceActionAttachments: state.dashboard.serviceActionAttachments,
        serviceActionPartImages: state.dashboard.serviceActionPartImages,
    };
};

const actions = {
    getServiceActionAttachments,
    clearServiceActionAttachments,
    addServiceActionAttachment,
    deleteServiceActionAttachment,
    getServiceActionPartImages,
    clearServiceActionPartImages,
    addServiceActionPartImage,
    deleteServiceActionPartImage,
};

export default connect(mapStateToProps, actions)(ServiceActionFormDialog);
